/**
 * AURA CSS Variables and Theme
 */

export const CSS_VARIABLES = `
  :host {
    /* Colors */
    --aura-primary: #1e3a5f;
    --aura-primary-hover: #2a4a73;
    --aura-primary-light: #e8eef5;
    --aura-secondary: #4a90d9;
    --aura-success: #28a745;
    --aura-warning: #ffc107;
    --aura-danger: #dc3545;

    /* FAB background defaults to primary in light mode */
    --aura-fab-bg: var(--aura-primary);
    --aura-fab-bg-hover: var(--aura-primary-hover);
    
    /* Background */
    --aura-bg: #ffffff;
    --aura-bg-secondary: #f8f9fa;
    --aura-bg-hover: #e9ecef;
    
    /* Text */
    --aura-text: #212529;
    --aura-text-muted: #6c757d;
    --aura-text-inverse: #ffffff;
    
    /* Borders */
    --aura-border: #dee2e6;
    --aura-border-focus: #4a90d9;
    
    /* Shadows */
    --aura-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
    --aura-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    --aura-shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.15);
    
    /* Spacing */
    --aura-spacing-xs: 4px;
    --aura-spacing-sm: 8px;
    --aura-spacing-md: 16px;
    --aura-spacing-lg: 24px;
    --aura-spacing-xl: 32px;
    
    /* Border radius */
    --aura-radius-sm: 4px;
    --aura-radius-md: 8px;
    --aura-radius-lg: 12px;
    --aura-radius-full: 9999px;
    
    /* Font */
    --aura-font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    --aura-font-size-sm: 12px;
    --aura-font-size-md: 14px;
    --aura-font-size-lg: 16px;
    --aura-font-size-xl: 18px;
    
    /* Transitions */
    --aura-transition-fast: 150ms ease;
    --aura-transition: 250ms ease;
    --aura-transition-slow: 350ms ease;
    
    /* Z-index */
    --aura-z-fab: 999998;
    --aura-z-panel: 999999;
    
    /* Sizes */
    --aura-fab-size: 56px;
    --aura-fab-bottom-offset: 50px;
    --aura-panel-width: 400px;
    --aura-panel-max-height: 90vh;
    --aura-touch-target: 44px;

    /*
     * Shield: block CSS inheritance from host page adjustments.
     * Properties like font-family, font-size, line-height, etc. set on
     * <body> by the accessibility modules would otherwise leak into the
     * Shadow DOM through CSS inheritance, breaking the widget layout.
     */
    font-family: var(--aura-font-family) !important;
    font-size: var(--aura-font-size-md) !important;
    line-height: 1.5 !important;
    letter-spacing: normal !important;
    word-spacing: normal !important;
    color: var(--aura-text) !important;
    text-align: left !important;
    text-transform: none !important;
    text-indent: 0 !important;
    text-decoration: none !important;
    font-weight: normal !important;
    font-style: normal !important;
    white-space: normal !important;
    direction: ltr !important;
    cursor: default !important;
    visibility: visible !important;
    opacity: 1 !important;

    /* Prevent ancestor filters/transforms from creating a containing
       block that traps fixed-positioned descendants (FAB, panel). */
    filter: none !important;
    transform: none !important;
  }

  /* Dark theme – very dark navy base inspired by the host app's dark
     palette.  Accent uses the secondary blue so icons/text pop without
     washing out the UI.  FAB keeps the brand blue via --aura-fab-bg. */
  :host([data-theme="dark"]) {
    --aura-bg: #0d1b2a;
    --aura-bg-secondary: #15283d;
    --aura-bg-hover: #1c3350;

    --aura-primary: #4a90d9;
    --aura-primary-hover: #5da3e6;
    --aura-primary-light: rgba(74, 144, 217, 0.12);

    --aura-fab-bg: #1e3a5f;
    --aura-fab-bg-hover: #2a4a73;

    --aura-text: #e2e8f0;
    --aura-text-muted: #94a3b8;
    --aura-text-inverse: #ffffff;
    --aura-border: #1e3a5f;
    --aura-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.4);
    --aura-shadow: 0 4px 6px rgba(0, 0, 0, 0.5);
    --aura-shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.6);
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    :host {
      --aura-transition-fast: 0ms;
      --aura-transition: 0ms;
      --aura-transition-slow: 0ms;
    }
  }
`;

export const RESET_STYLES = `
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  button {
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    border: none;
    background: none;
  }
  
  :focus-visible {
    outline: 2px solid var(--aura-border-focus);
    outline-offset: 2px;
  }
`;
